import { type OperationVariables } from '@apollo/react-common';
import { type GraphQLTaggedNode } from 'react-relay';
import { createOperationDescriptor, getRequest } from 'relay-runtime';

import { type QueryInteropFetchPolicy } from '../mapFetchPolicyForQuery.ts';
import { toDocumentNode } from '../toDocumentNode.ts';

import {
	type CacheBridgeCache,
	type CacheWriteResult as CommonCacheWriteResult,
	getFailureContext,
	getSecondaryCache,
	skipped,
	writeSafely,
	written,
} from './cacheBridgeCommon.ts';
import { getApolloRelayCacheBridgeDisabledReason } from './cacheBridgeGate.ts';
import { addRelayConcreteTypenamesToQueryData } from './queryResultTypenames.ts';

export type CacheWriteResult = CommonCacheWriteResult;

export type QueryCacheBridgeSource = CacheBridgeCache;

export type QueryCacheBridgeWriteArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = {
	query: GraphQLTaggedNode;
	variables?: TVariables;
	data?: TData | null;
	fetchPolicy?: QueryInteropFetchPolicy;
	useAggClient?: boolean;
};

export type QueryCacheBridgeSyncArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = QueryCacheBridgeWriteArgs<TData, TVariables> & {
	source: QueryCacheBridgeSource;
	error?: unknown;
	skip?: boolean;
};

function shouldSkipSyncCache<TData, TVariables extends Record<string, any>>({
	data,
	fetchPolicy,
}: QueryCacheBridgeWriteArgs<TData, TVariables>): string | null {
	if (fetchPolicy === 'no-cache') return 'no-cache';
	if (data == null) return 'data-missing';
	if (typeof window === 'undefined') return 'window-missing';

	return null;
}

function getApolloClient(useAggClient?: boolean) {
	if (typeof window === 'undefined') return undefined;

	if (useAggClient) {
		return window['GLOBAL_APOLLO_AGG_CLIENT'] ?? (window as any)['MOCK_GATEWAY_APOLLO_CLIENT'];
	}

	return window['GLOBAL_APOLLO_CLIENT'];
}

function writeQueryToApollo<TData, TVariables extends Record<string, any> = OperationVariables>(
	args: QueryCacheBridgeWriteArgs<TData, TVariables>,
): CacheWriteResult {
	const skipReason = shouldSkipSyncCache(args);
	if (skipReason) return skipped(skipReason);

	const client = getApolloClient(args.useAggClient);
	if (!client) return skipped('apollo-client-missing');

	return writeSafely(
		() => {
			const data = addRelayConcreteTypenamesToQueryData(args.query, args.data as TData);

			// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- cache bridge intentionally mirrors Relay query results into Apollo during migration
			client.writeQuery({
				query: toDocumentNode(args.query),
				variables: args.variables,
				data,
			});
			return written();
		},
		getFailureContext('query', 'write', 'apollo'),
	);
}

function writeQueryToRelay<TData, TVariables extends Record<string, any> = OperationVariables>(
	args: QueryCacheBridgeWriteArgs<TData, TVariables>,
): CacheWriteResult {
	const skipReason = shouldSkipSyncCache(args);
	if (skipReason) return skipped(skipReason);

	const environment = window['GLOBAL_RELAY_ENV'];
	if (!environment) return skipped('relay-environment-missing');

	return writeSafely(
		() => {
			const operation = createOperationDescriptor(getRequest(args.query), args.variables ?? {});
			environment.commitPayload(operation, args.data as Record<string, unknown>);
			return written();
		},
		getFailureContext('query', 'write', 'relay'),
	);
}

function writeQueryToCache<TData, TVariables extends Record<string, any> = OperationVariables>(
	args: QueryCacheBridgeWriteArgs<TData, TVariables>,
	targetCache: CacheBridgeCache,
): CacheWriteResult {
	return targetCache === 'apollo' ? writeQueryToApollo(args) : writeQueryToRelay(args);
}

export function syncQueryCacheBridge<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>({
	source,
	error,
	skip,
	...writeArgs
}: QueryCacheBridgeSyncArgs<TData, TVariables>): CacheWriteResult {
	// Query mirroring is secondary work: only successful, non-skipped primary
	// operations should attempt to populate the opposite cache.
	if (skip) return skipped('operation-skipped');
	if (error) return skipped('operation-error');

	const disabledReason = getApolloRelayCacheBridgeDisabledReason();
	if (disabledReason) return skipped(disabledReason);

	return writeQueryToCache(writeArgs, getSecondaryCache(source));
}
